// UmpireShell — dedicated shell for users with the UMPIRE role.
//
// A user with only UMPIRE lands here at login; a user with UMPIRE plus
// other roles (say, PLAYER + UMPIRE) picks this workspace from the
// login segmented control (or the workspace-switcher in the topbar).
//
// One primary view today — My Polls — with the option to grow later
// (Assignments history, Availability calendar, etc.).

import React, { useState } from "react";
import {
  Gavel, LogOut, ArrowLeftRight, Sun, Moon, Menu, X
} from "lucide-react";
import { logout } from "../api";
import { useTheme } from "./Theme";
import { useWorkspace } from "./Workspace";
import { useVersion } from "./Version";
import { canEnterWorkspace } from "../data";
import { useToast } from "./Toast";

import UmpirePolls from "../views/UmpirePolls";

export default function UmpireShell({ user, setUser }) {
  const { theme, toggleTheme } = useTheme();
  const { setWorkspace }       = useWorkspace();
  const { version }            = useVersion();
  const toast                  = useToast();
  const isMobile               = version === "v4";
  const [drawerOpen, setDrawerOpen] = useState(false);

  function signOut() { logout(); setUser(null); }
  const canGoGabruz = canEnterWorkspace(user, "gabruz");
  const canGoLeague = canEnterWorkspace(user, "league");
  function switchTo(ws) {
    setWorkspace(ws);
    toast.success(ws === "gabruz" ? "Switched to Gabruz Universe." : "Switched to League.");
  }

  const body = <UmpirePolls/>;

  if (isMobile) {
    return (
      <div className="v4-shell league-shell league-shell--v4 ump-shell ump-shell--v4">
        <header className="v4-appbar">
          <div className="v4-appbar-brand">
            <Gavel size={16}/>
            <div>
              <small>UMPIRE</small>
              <strong>My Polls</strong>
            </div>
          </div>
          <div className="v4-appbar-actions">
            <button
              type="button"
              className="v4-iconBtn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            ><Menu size={20}/></button>
          </div>
        </header>

        <main className="v4-main">{body}</main>

        {drawerOpen && (
          <>
            <div className="v4-sheetBackdrop" onClick={() => setDrawerOpen(false)}/>
            <aside className="v4-sheet" aria-label="Menu">
              <div className="v4-sheet-handle" aria-hidden="true"/>
              <div className="v4-sheet-head">
                <div className="v4-sheet-user">
                  <div className="avatar">{user.avatar}</div>
                  <div>
                    <strong>{user.name}</strong>
                    <small>{user.email}</small>
                  </div>
                </div>
                <button type="button" className="v4-iconBtn" onClick={() => setDrawerOpen(false)} aria-label="Close">
                  <X size={18}/>
                </button>
              </div>
              <ul>
                {canGoGabruz && (
                  <li>
                    <button type="button" className="v4-sheet-link"
                      onClick={() => { setDrawerOpen(false); switchTo("gabruz"); }}>
                      <ArrowLeftRight size={16}/>
                      <span>Switch to Gabruz Universe</span>
                    </button>
                  </li>
                )}
                {canGoLeague && (
                  <li>
                    <button type="button" className="v4-sheet-link"
                      onClick={() => { setDrawerOpen(false); switchTo("league"); }}>
                      <ArrowLeftRight size={16}/>
                      <span>Switch to League</span>
                    </button>
                  </li>
                )}
                <li>
                  <button type="button" className="v4-sheet-link" onClick={toggleTheme}>
                    {theme === "light" ? <Moon size={16}/> : <Sun size={16}/>}
                    <span>Switch to {theme === "light" ? "dark" : "light"} theme</span>
                  </button>
                </li>
                <li>
                  <button type="button" className="v4-sheet-link v4-sheet-link--danger" onClick={signOut}>
                    <LogOut size={16}/>
                    <span>Sign out</span>
                  </button>
                </li>
              </ul>
            </aside>
          </>
        )}
      </div>
    );
  }

  // Desktop
  return (
    <div className="league-shell ump-shell">
      <aside className="league-sidebar">
        <div className="league-sidebar-brand">
          <Gavel size={20}/>
          <div>
            <small>PITTSBURGH</small>
            <strong>Umpire Portal</strong>
          </div>
        </div>

        <nav className="league-sidebar-nav" aria-label="Umpire">
          <button
            type="button"
            className="league-sidebar-item league-sidebar-item--active"
            aria-current="page"
          >
            <Gavel size={16}/>
            <span>My Polls</span>
          </button>
        </nav>

        <div className="league-sidebar-foot">
          {(canGoGabruz || canGoLeague) && (
            <div className="ump-shell-switchGroup">
              {canGoGabruz && (
                <button
                  type="button"
                  className="league-sidebar-switch"
                  onClick={() => switchTo("gabruz")}
                >
                  <ArrowLeftRight size={14}/>
                  <span>Switch to Gabruz Universe</span>
                </button>
              )}
              {canGoLeague && (
                <button
                  type="button"
                  className="league-sidebar-switch"
                  onClick={() => switchTo("league")}
                >
                  <ArrowLeftRight size={14}/>
                  <span>Switch to League</span>
                </button>
              )}
            </div>
          )}
          <div className="league-sidebar-user">
            <div className="league-sidebar-user-avatar">{user.avatar}</div>
            <div className="league-sidebar-user-info">
              <strong>{user.name}</strong>
              <small>Umpire</small>
            </div>
            <button
              type="button"
              className="league-sidebar-user-btn"
              onClick={toggleTheme}
              title={theme === "light" ? "Dark mode" : "Light mode"}
              aria-label="Toggle theme"
            >
              {theme === "light" ? <Moon size={14}/> : <Sun size={14}/>}
            </button>
            <button
              type="button"
              className="league-sidebar-user-btn"
              onClick={signOut}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={14}/>
            </button>
          </div>
        </div>
      </aside>

      <main className="league-main">{body}</main>
    </div>
  );
}
